import type { ComponentProps } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  AlignSelfProps,
  BackgroundColorProps,
  BorderColorProps,
  DisplayProps,
  FlexProps,
  HeightProps,
  JustifyContentProps,
  MarginLeftProps,
  MarginRightProps,
  MaxWidthProps,
  PaddingProps,
  SizeProps,
  WidthProps,
} from 'styled-system';
import {
  alignItems,
  alignSelf,
  backgroundColor,
  borderColor,
  display,
  height,
  justifyContent,
  marginLeft,
  marginRight,
  maxWidth,
  padding,
  size,
  themeGet,
  width,
} from 'styled-system';

import type { FillProps } from '../../../theme/system-types';
import type {
  FlexShrinkProps,
  OutlineColorProps,
  PointerEventProps,
  TransformProps,
} from '../../../theme/system-utilities';
import {
  fill,
  flexShrink,
  outlineColor,
  pointerEvents,
  transform,
} from '../../../theme/system-utilities';
import { ButtonBase } from '../../Button';
import Icon from '../../Icon';
import ImageOption from '../../ImageOption';
import { BodyRegularBR2, CaptionRegularCR2 } from '../../Typography';
import {
  IMAGE_MARGIN,
  SWATCH_GAP_SIZE,
  SWATCH_IMAGE_BORDER_COLOR,
  SWATCH_IMAGE_OUTLINE_COLOR,
  SWATCH_IMAGE_SIZE,
  SWATCH_PILL_CONTAINER_HEIGHT,
} from '../constants/constants';

interface Swatch {
  imageUrl: string | null;
  key: string;
}

export interface SwatchPillProps {
  swatches: Swatch[];
  allSwatchesCount: number;
  visibleSwatchesCount: number;
  isMobile: boolean;
  ariaLabel: string;
  swatchContainerStyles?: Partial<ComponentProps<typeof SwatchesContainer>>;
  imageOptionStylesOverride?: Partial<ComponentProps<typeof StyledImageOption>>;
  swatchImageSize?: number;
  swatchGapSize?: number;
  arrowIconStyles?: Partial<ComponentProps<typeof ArrowRightIcon>>;
  withCompactMoreSwatches?: boolean;
  swatchImageFlexShrink?: number;
  withFixedMobileWidth?: boolean;
}

const swatchesContainerBaseStyles: ComponentProps<typeof ButtonBase> & MaxWidthProps = {
  alignItems: 'center',
  background: 'sys.neutral.background.default',
  border: '1px solid',
  borderColor: 'sys.neutral.border.hover',
  borderRadius: 'x-large',
  cursor: 'pointer',
  display: 'flex',
  maxWidth: '156px',
};

export const SwatchesContainer = styled(ButtonBase)<
  FlexProps &
    BorderColorProps &
    HeightProps &
    PaddingProps &
    JustifyContentProps &
    MaxWidthProps &
    WidthProps & {
      smallerVariation?: boolean;
    }
>`
  position: relative;
  background-color: ${themeGet('colors.sys.neutral.background.default')};
  ${height};
  ${justifyContent};
  ${maxWidth};
  padding-top: ${themeGet('space.sp_3')}px;
  padding-bottom: ${themeGet('space.sp_3')}px;
  padding-left: ${themeGet('space.sp_8')}px;
  padding-right: ${themeGet('space.sp_8')}px;
  ${padding};
  ${width};

  &:hover {
    background-color: ${themeGet('colors.sys.neutral.background.hover')};
  }

  &:active {
    background-color: ${themeGet('colors.sys.neutral.background.active')};
  }

  &:focus-visible {
    background-color: ${themeGet('colors.sys.neutral.background.hover')};
  }

  ${props =>
    props.smallerVariation &&
    css`
      width: 130px;
    `}
`;

const StyledImageOption = styled(ImageOption)<
  MarginRightProps & BackgroundColorProps & BorderColorProps & FlexShrinkProps & OutlineColorProps
>`
  ${marginRight};
  ${backgroundColor};
  ${borderColor};
  outline: 1px solid;
  ${outlineColor};
  ${flexShrink};
`;

const imageOptionStyles: Partial<ComponentProps<typeof StyledImageOption>> = {
  marginRight: '-3px',
  backgroundColor: 'white',
  borderColor: SWATCH_IMAGE_BORDER_COLOR,
  outlineColor: SWATCH_IMAGE_OUTLINE_COLOR,
};

const MoreSwatches = styled(BodyRegularBR2)<
  AlignSelfProps & DisplayProps & AlignItemsProps & HeightProps
>`
  ${alignSelf};
  ${display};
  ${alignItems};
  ${height};
`;

const MoreSwatchesCompact = styled(CaptionRegularCR2)<
  AlignSelfProps & DisplayProps & AlignItemsProps & HeightProps
>`
  ${alignSelf};
  ${display};
  ${alignItems};
  ${height};
`;

const moreSwatchesStyles: ComponentProps<typeof MoreSwatches> = {
  ml: 'sp_8',
  display: 'flex',
  alignItems: 'center',
  height: '100%',
};

const ArrowRightIcon = styled(Icon)<
  SizeProps &
    PaddingProps &
    FillProps &
    MarginLeftProps &
    TransformProps &
    PointerEventProps &
    FlexShrinkProps
>`
  ${size};
  ${padding};
  ${fill};
  ${marginLeft};
  ${transform};
  ${pointerEvents};
  ${flexShrink};
`;

const defaultArrowRightIconStyles: ComponentProps<typeof ArrowRightIcon> = {
  size: 12,
  fill: 'sys.neutral.icon.default',
  name: 'arrowDownStrong',
  p: 'sp_6',
  marginLeft: 'auto',
  transform: 'rotate(270deg)',
  pointerEvents: 'none',
  flexShrink: 0,
};

const SwatchPill = ({
  swatches,
  allSwatchesCount,
  visibleSwatchesCount,
  isMobile,
  ariaLabel,
  swatchContainerStyles,
  imageOptionStylesOverride,
  swatchImageSize = SWATCH_IMAGE_SIZE,
  swatchGapSize = SWATCH_GAP_SIZE,
  arrowIconStyles,
  withCompactMoreSwatches,
  swatchImageFlexShrink,
  withFixedMobileWidth = true,
}: SwatchPillProps) => {
  const MoreSwatchesComponent = withCompactMoreSwatches ? MoreSwatchesCompact : MoreSwatches;
  const resolvedSwatchImageStyle = { ...imageOptionStyles, ...imageOptionStylesOverride };

  return (
    <SwatchesContainer
      {...swatchesContainerBaseStyles}
      height={SWATCH_PILL_CONTAINER_HEIGHT}
      justifyContent="center"
      {...swatchContainerStyles}
      aria-label={ariaLabel}
      smallerVariation={withFixedMobileWidth && isMobile}
    >
      {swatches.slice(0, visibleSwatchesCount).map(({ imageUrl, key }, index) => (
        <StyledImageOption
          {...resolvedSwatchImageStyle}
          key={index}
          url={imageUrl}
          alt=""
          size={swatchImageSize}
          gapSize={swatchGapSize}
          mr={`${IMAGE_MARGIN}px`}
          dataType={key}
          loading="lazy"
          flexShrink={swatchImageFlexShrink}
        />
      ))}
      {allSwatchesCount > visibleSwatchesCount && (
        <MoreSwatchesComponent {...moreSwatchesStyles} data-type="more" alignSelf="end">
          +{allSwatchesCount - visibleSwatchesCount}
        </MoreSwatchesComponent>
      )}
      <ArrowRightIcon {...defaultArrowRightIconStyles} {...arrowIconStyles} />
    </SwatchesContainer>
  );
};

export default SwatchPill;
